import { useState, useMemo, useEffect } from "react";
import {
  Building2,
  Palette,
  Bell,
  Upload,
  Save,
  Check,
  Sparkles,
  Smartphone,
  X,
  ChevronRight,
  Mail,
  MessageSquare,
  BellRing,
  Workflow,
  Users2,
  Monitor,
  Sun,
  Moon,
  Laptop2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AGENCY } from "@/data/agency";
import IPhonePreview from "@/components/settings/IPhonePreview";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";

// ---------------- UI primitives ----------------
const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[12px] font-medium text-[#111111]">{label}</span>
    {hint && (
      <span className="block text-[11.5px] text-[#6B7280] mt-0.5">{hint}</span>
    )}
    <div className="mt-2">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full h-11 rounded-xl bg-white border border-[#EEF2F5] px-3.5 text-[13.5px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors"
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className="w-full min-h-[88px] rounded-xl bg-white border border-[#EEF2F5] px-3.5 py-3 text-[13.5px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors resize-none"
  />
);

const Toggle = ({ checked, onChange, testId }) => (
  <button
    type="button"
    data-testid={testId}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={[
      "relative h-6 w-10 rounded-full transition-colors",
      checked ? "bg-[#111111]" : "bg-[#E5E7EB]",
    ].join(" ")}
  >
    <span
      className={[
        "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all",
        checked ? "left-[18px]" : "left-0.5",
      ].join(" ")}
    />
  </button>
);

// ---------------- Sections ----------------
const NAV = [
  { key: "identity",       label: "Identité",              icon: Building2 },
  { key: "appearance",     label: "Apparence",             icon: Palette },
  { key: "customization",  label: "Personnalisation client", icon: Sparkles },
  { key: "notifications",  label: "Notifications",         icon: Bell },
  { key: "team",           label: "Équipe",                icon: Users2,        external: "/app/parametres/equipe" },
  { key: "communications", label: "Communications",        icon: MessageSquare, external: "/app/parametres/communications" },
  { key: "workflows",      label: "Workflows",             icon: Workflow,      external: "/app/parametres/workflows" },
  { key: "ecrans",         label: "Écrans agence",         icon: Monitor,       external: "/app/parametres/ecrans" },
];

// ─────────────────────────────────────────────────────────────────────────
// Section "Apparence" — Sprint V2 wyvio
// ─────────────────────────────────────────────────────────────────────────
const AppearanceSection = () => {
  const { preference, effective, setPreference } = useTheme();
  const options = [
    { key: "light", icon: Sun,     label: "Clair",       hint: "Identité wyvio par défaut." },
    { key: "dark",  icon: Moon,    label: "Sombre",      hint: "Pour les vues prolongées." },
    { key: "auto",  icon: Laptop2, label: "Automatique", hint: "Suit la préférence système." },
  ];
  return (
    <div className="space-y-6" data-testid="section-appearance">
      <div>
        <p className="text-[13px] font-semibold text-[#111111] dark:text-white">Thème</p>
        <p className="mt-1 text-[12px] text-[#6B7280] dark:text-[#A3A3A3] leading-relaxed max-w-lg">
          Le thème clair reste l&apos;identité par défaut de wyvio. Choisis le
          mode sombre uniquement si tu le préfères — la préférence système
          reste toujours accessible.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup"
           aria-label="Choix du thème">
        {options.map(({ key, icon: Icon, label, hint }) => {
          const active = preference === key;
          return (
            <button
              key={key}
              role="radio"
              aria-checked={active}
              onClick={() => setPreference(key)}
              data-testid={`theme-option-${key}`}
              className={[
                "text-left rounded-2xl border p-4 transition-all duration-200",
                "bg-white dark:bg-[#111111]",
                active
                  ? "border-[#0A0A0A] dark:border-white ring-2 ring-[#C8FF2F]"
                  : "border-[#EEF2F5] dark:border-[#262626] hover:border-[#DCE1E7] dark:hover:border-[#3A3A3A]",
              ].join(" ")}
            >
              <div className="flex items-center justify-between">
                <Icon size={16} strokeWidth={2} className="text-[#0A0A0A] dark:text-white" />
                {active && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#0A0A0A] dark:text-white">
                    Actif
                  </span>
                )}
              </div>
              <p className="mt-3 text-[13.5px] font-semibold text-[#0A0A0A] dark:text-white">
                {label}
              </p>
              <p className="mt-1 text-[11.5px] text-[#6B7280] dark:text-[#A3A3A3] leading-relaxed">
                {hint}
              </p>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-[#9CA3AF] dark:text-[#737373]" data-testid="appearance-effective">
        Mode appliqué actuellement : <span className="font-semibold text-[#0A0A0A] dark:text-white capitalize">{effective === "light" ? "clair" : "sombre"}</span>
      </div>
    </div>
  );
};

const IdentitySection = ({ config, update, uploadLogo, logoUploading }) => {
  const { identity } = config;
  const set = (k, v) => update({ identity: { ...identity, [k]: v } });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Nom de l'agence">
          <Input
            data-testid="settings-name"
            value={identity.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Ex. SimpliciCar Toulon Est"
          />
        </Field>
        <Field label="Site internet">
          <Input
            data-testid="settings-website"
            value={identity.website}
            onChange={(e) => set("website", e.target.value)}
            placeholder="simplicicar-toulon.fr"
          />
        </Field>
        <Field label="Téléphone">
          <Input
            data-testid="settings-phone"
            value={identity.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>
        <Field label="Adresse e-mail">
          <Input
            data-testid="settings-email"
            type="email"
            value={identity.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field label="Adresse postale" hint="Affichée en signature">
          <Input
            data-testid="settings-address"
            value={identity.address}
            onChange={(e) => set("address", e.target.value)}
          />
        </Field>
        <Field label="Logo" hint="PNG/JPG/WEBP/SVG · max 5 MB">
          <label
            data-testid="settings-logo-upload"
            className="w-full h-11 rounded-xl border border-dashed border-[#E5E7EB] hover:border-[#111111] hover:bg-[#F7F8FA] flex items-center justify-center gap-2 text-[#6B7280] hover:text-[#111111] transition-all cursor-pointer"
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                uploadLogo?.(f);
                e.target.value = "";
              }}
            />
            <Upload size={14} strokeWidth={1.75} />
            <span className="text-[13px] font-medium">
              {logoUploading
                ? "Envoi en cours…"
                : identity.logo ? "Remplacer le logo" : "Téléverser un fichier"}
            </span>
          </label>
        </Field>
      </div>
    </div>
  );
};

const CustomizationSection = ({ config, update }) => {
  const { client } = config;
  const set = (k, v) => update({ client: { ...client, [k]: v } });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5">
        <Field label="Titre d'accueil" hint="Affiché au-dessus du &lsquo;Bonjour&rsquo;">
          <Input
            data-testid="settings-welcome-title"
            value={client.welcomeTitle}
            onChange={(e) => set("welcomeTitle", e.target.value)}
            placeholder="Votre espace wyvio"
          />
        </Field>

        <Field label="Message de bienvenue">
          <Textarea
            data-testid="settings-welcome-message"
            value={client.welcomeMessage}
            onChange={(e) => set("welcomeMessage", e.target.value)}
            placeholder="Votre véhicule avance normalement. Nous vous tenons informé à chaque étape."
          />
        </Field>

        <Field label="Message de fin de dossier" hint="Affiché quand le CPI est disponible">
          <Textarea
            data-testid="settings-end-message"
            value={client.endMessage}
            onChange={(e) => set("endMessage", e.target.value)}
            placeholder="Merci pour votre confiance. Votre dossier est maintenant terminé…"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-5 pt-4 border-t border-[#EEF2F5]">
        <Field label="Nom du conseiller affiché" hint="La photo utilisée dans l'espace client est votre photo de profil utilisateur.">
          <Input
            data-testid="settings-advisor-name"
            value={client.advisorName}
            onChange={(e) => set("advisorName", e.target.value)}
            placeholder="Julien Petit"
          />
        </Field>
      </div>

      <div className="pt-4 border-t border-[#EEF2F5]">
        <div className="flex items-center justify-between rounded-2xl bg-[#F7F8FA] p-4">
          <div className="flex-1">
            <p className="text-[13.5px] font-semibold text-[#111111]">
              Afficher mes coordonnées au client
            </p>
            <p className="mt-0.5 text-[11.5px] text-[#6B7280]">
              Téléphone et e-mail visibles dans l&apos;espace client.
            </p>
          </div>
          <Toggle
            testId="settings-show-contact"
            checked={client.showContact}
            onChange={(v) => set("showContact", v)}
          />
        </div>
      </div>
    </div>
  );
};

const NotificationsSection = ({ config, update }) => {
  const { notifications } = config;
  const set = (k, v) => update({ notifications: { ...notifications, [k]: v } });

  const items = [
    { key: "email", label: "E-mail", desc: "Notifications par e-mail pour chaque étape validée.", icon: Mail },
    { key: "sms",   label: "SMS",    desc: "Alerte SMS pour les événements importants (livraison, blocage).", icon: MessageSquare },
    { key: "push",  label: "Push",   desc: "Notifications push mobile (bientôt disponible).", icon: BellRing, soon: true },
  ];

  return (
    <div className="space-y-3">
      {items.map(({ key, label, desc, icon: Icon, soon }) => (
        <div
          key={key}
          className="rounded-2xl bg-white border border-[#EEF2F5] p-4 flex items-center gap-4"
        >
          <div className="h-10 w-10 rounded-xl bg-[#F7F8FA] flex items-center justify-center text-[#111111] shrink-0">
            <Icon size={16} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[13.5px] font-semibold text-[#111111]">
                {label}
              </p>
              {soon && (
                <span className="rounded-full bg-[#F7F8FA] px-2 py-0.5 text-[9.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Bientôt
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[12px] text-[#6B7280]">{desc}</p>
          </div>
          <Toggle
            testId={`settings-notif-${key}`}
            checked={notifications[key]}
            onChange={(v) => set(key, v)}
          />
        </div>
      ))}

      <div className="mt-6 rounded-2xl bg-[#111111] text-white p-5">
        <div className="flex items-center gap-2">
          <Bell size={13} strokeWidth={2} className="text-[#C8FF2F]" />
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#C8FF2F]">
            Automatisation
          </p>
        </div>
        <p className="mt-2 text-[13.5px] font-semibold">
          Envoi automatique à chaque changement d&apos;étape
        </p>
        <p className="mt-1 text-[12px] text-white/70 leading-relaxed">
          Vos clients sont notifiés en temps réel quand une étape passe en {`"Terminé"`}. Aucune action manuelle nécessaire.
        </p>
      </div>
    </div>
  );
};

// ---------------- Checklist ----------------
const useChecklist = (config, touched) => {
  const { identity, client } = config;
  return useMemo(
    () => [
      { key: "logo", label: "Logo ajouté", done: Boolean(identity.logo) },
      {
        key: "coord",
        label: "Coordonnées complètes",
        done: Boolean(identity.phone && identity.email && identity.address),
      },
      {
        key: "advisor",
        label: "Conseiller renseigné",
        done: Boolean(client.advisorName),
      },
      {
        key: "welcome",
        label: "Message de bienvenue",
        done: Boolean(client.welcomeMessage && client.welcomeMessage.trim().length > 10),
      },
    ],
    [identity, client, touched]
  );
};

const ChecklistCard = ({ items }) => {
  const doneCount = items.filter((i) => i.done).length;
  const all = doneCount === items.length;

  return (
    <div
      data-testid="settings-checklist"
      className={[
        "rounded-2xl p-5 border transition-all",
        all
          ? "bg-gradient-to-br from-[#111111] to-[#1F2937] text-white border-transparent"
          : "bg-white border-[#EEF2F5]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between">
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${all ? "text-[#C8FF2F]" : "text-[#6B7280]"}`}>
          Personnalisation
        </p>
        <span className={`text-[12px] font-semibold tabular-nums ${all ? "text-white" : "text-[#111111]"}`}>
          {doneCount}/{items.length}
        </span>
      </div>

      {all ? (
        <div className="mt-3 flex items-start gap-2.5">
          <Sparkles size={16} strokeWidth={2} className="text-[#C8FF2F] mt-0.5" />
          <div>
            <p className="text-[15px] font-semibold tracking-tight">
              Votre espace client est prêt.
            </p>
            <p className="mt-1 text-[12px] text-white/70 leading-relaxed">
              Vous pouvez inviter vos premiers clients en toute confiance.
            </p>
          </div>
        </div>
      ) : (
        <p className={`mt-2 text-[13px] leading-relaxed ${all ? "text-white/80" : "text-[#374151]"}`}>
          Complétez ces étapes pour un espace client 100 % à vos couleurs.
        </p>
      )}

      <ul className="mt-4 space-y-2">
        {items.map((it) => (
          <li
            key={it.key}
            className={[
              "flex items-center gap-2.5 text-[12.5px] transition-all",
              it.done ? "opacity-100" : "opacity-70",
            ].join(" ")}
          >
            <span
              className={[
                "h-4 w-4 rounded-full flex items-center justify-center shrink-0",
                it.done
                  ? "bg-[#C8FF2F] text-[#111111]"
                  : all
                  ? "bg-white/10 text-white/40"
                  : "bg-[#F3F4F6] text-[#9CA3AF]",
              ].join(" ")}
            >
              {it.done && <Check size={10} strokeWidth={3} />}
            </span>
            <span
              className={[
                it.done ? "font-medium" : "",
                all ? "text-white" : "text-[#111111]",
              ].join(" ")}
            >
              {it.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------------- Main page ----------------
const DEFAULT_CONFIG = {
  identity: {
    name: AGENCY.name,
    logo: null,
    phone: AGENCY.phone,
    email: AGENCY.email,
    address: AGENCY.address,
    website: "simplicicar-toulon.fr",
    primaryColor: "#C8FF2F",
    secondaryColor: "#D9FFE8",
  },
  client: {
    welcomeTitle: "Votre espace wyvio",
    welcomeMessage:
      "Votre véhicule avance normalement. Nous vous tenons informé à chaque étape.",
    endMessage:
      "Merci pour votre confiance. Votre dossier est maintenant terminé. Vous pouvez toujours consulter vos documents et l'historique.",
    advisorName: "Julien Petit",
    advisorPhoto: null,
    showContact: true,
  },
  notifications: {
    email: true,
    sms: true,
    push: false,
  },
};

const Settings = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [section, setSection] = useState("identity");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Hydrate from backend on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [agency, me] = await Promise.all([
          api.getAgency(),
          api.getMe().catch(() => null),
        ]);
        if (cancelled) return;
        const s = agency?.settings || {};
        setConfig({
          identity: {
            name: agency?.name || DEFAULT_CONFIG.identity.name,
            logo: agency?.logo_url || null,
            phone: agency?.phone || DEFAULT_CONFIG.identity.phone,
            email: agency?.email || DEFAULT_CONFIG.identity.email,
            address: agency?.address || DEFAULT_CONFIG.identity.address,
            website: agency?.website || DEFAULT_CONFIG.identity.website,
            primaryColor: s.primary_color || DEFAULT_CONFIG.identity.primaryColor,
            secondaryColor: s.secondary_color || DEFAULT_CONFIG.identity.secondaryColor,
          },
          client: {
            welcomeTitle: s.welcome_title || DEFAULT_CONFIG.client.welcomeTitle,
            welcomeMessage: s.welcome_message || DEFAULT_CONFIG.client.welcomeMessage,
            endMessage: s.end_message || DEFAULT_CONFIG.client.endMessage,
            advisorName: s.advisor_name || me?.name || DEFAULT_CONFIG.client.advisorName,
            advisorPhoto: me?.photo_url || null,
            showContact: s.show_contact !== false,
          },
          notifications: {
            email: s.notif_email !== false,
            sms: s.notif_sms !== false,
            push: s.notif_push === true,
          },
        });
      } catch (e) {
        if (!cancelled) toast.error("Impossible de charger les paramètres.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (patch) => {
    setConfig((c) => ({ ...c, ...patch }));
  };

  const checklist = useChecklist(config, {});

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.updateAgency({
        name: config.identity.name,
        phone: config.identity.phone,
        email: config.identity.email,
        address: config.identity.address,
        website: config.identity.website,
        logo_url: config.identity.logo,
        settings: {
          primary_color: config.identity.primaryColor,
          secondary_color: config.identity.secondaryColor,
          welcome_title: config.client.welcomeTitle,
          welcome_message: config.client.welcomeMessage,
          end_message: config.client.endMessage,
          advisor_name: config.client.advisorName,
          show_contact: config.client.showContact,
          notif_email: config.notifications.email,
          notif_sms: config.notifications.sms,
          notif_push: config.notifications.push,
        },
      });
      setSaved(true);
      toast.success("Paramètres enregistrés.");
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    setLogoUploading(true);
    try {
      const res = await api.uploadAgencyLogo(file);
      update({ identity: { ...config.identity, logo: res.logo_url } });
      toast.success("Logo mis en ligne.");
    } catch (e) {
      toast.error(e.message || "Upload logo impossible.");
    } finally {
      setLogoUploading(false);
    }
  };

  const Section =
    section === "identity"
      ? IdentitySection
      : section === "appearance"
      ? AppearanceSection
      : section === "customization"
      ? CustomizationSection
      : NotificationsSection;

  const activeMeta = NAV.find((n) => n.key === section);

  return (
    <div className="wyvio-fade-in" data-testid="page-settings">
      {/* Header */}
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-[12.5px] font-medium text-[#6B7280]">
            {config.identity.name}
          </p>
          <h1 className="mt-1.5 text-[32px] leading-tight font-semibold tracking-tight text-[#111111]">
            Paramètres
          </h1>
          <p className="mt-2 text-[14px] text-[#6B7280] max-w-xl">
            Personnalisez entièrement votre espace wyvio. Toute modification est visible en temps réel dans l&apos;aperçu client.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreviewOpen(true)}
            data-testid="settings-open-preview"
            className="xl:hidden h-11 inline-flex items-center gap-2 rounded-full bg-white border border-[#EEF2F5] text-[#111111] text-[13px] font-medium px-4 hover:bg-[#F7F8FA] transition-colors"
          >
            <Smartphone size={14} strokeWidth={2} />
            Voir mon espace client
          </button>
          <button
            onClick={handleSave}
            data-testid="settings-save"
            disabled={saving || loading}
            className="h-11 inline-flex items-center gap-2 rounded-full bg-[#111111] text-white text-[13px] font-semibold px-5 hover:bg-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>Enregistrement…</>
            ) : saved ? (
              <>
                <Check size={14} strokeWidth={2.5} className="text-[#C8FF2F]" />
                Enregistré
              </>
            ) : (
              <>
                <Save size={14} strokeWidth={2} />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </header>

      <div className="mt-8 grid grid-cols-1 xl:grid-cols-[220px_1fr_360px] gap-8">
        {/* Sub-nav */}
        <aside data-testid="settings-nav" className="xl:sticky xl:top-4 self-start">
          <ul className="flex flex-row xl:flex-col gap-1.5 overflow-x-auto">
            {NAV.map(({ key, label, icon: Icon, external }) => {
              const isActive = section === key;
              return (
                <li key={key}>
                  <button
                    onClick={() => external ? navigate(external) : setSection(key)}
                    data-testid={`settings-nav-${key}`}
                    className={[
                      "w-full inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] font-medium transition-all whitespace-nowrap",
                      isActive
                        ? "bg-[#111111] text-white"
                        : "text-[#6B7280] hover:text-[#111111] hover:bg-white",
                    ].join(" ")}
                  >
                    <Icon size={15} strokeWidth={1.75} />
                    <span className="flex-1 text-left">{label}</span>
                    {isActive && (
                      <ChevronRight size={13} strokeWidth={2} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Checklist desktop */}
          <div className="hidden xl:block mt-6">
            <ChecklistCard items={checklist} />
          </div>
        </aside>

        {/* Content */}
        <main className="min-w-0">
          <div className="wyvio-card p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              {activeMeta && (
                <div className="h-10 w-10 rounded-xl bg-[#F7F8FA] flex items-center justify-center text-[#111111]">
                  <activeMeta.icon size={17} strokeWidth={1.75} />
                </div>
              )}
              <div>
                <h2 className="text-[16px] font-semibold text-[#111111] tracking-tight">
                  {activeMeta?.label}
                </h2>
              </div>
            </div>
            <Section
              config={config}
              update={update}
              uploadLogo={uploadLogo}
              logoUploading={logoUploading}
            />
          </div>

          {/* Checklist mobile */}
          <div className="xl:hidden mt-6">
            <ChecklistCard items={checklist} />
          </div>
        </main>

        {/* Live preview desktop */}
        <aside className="hidden xl:block" data-testid="settings-preview-desktop">
          <div className="sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
                  Aperçu client
                </p>
                <p className="mt-0.5 text-[12px] text-[#111111]">
                  Ce que verra votre client
                </p>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[10px] font-semibold text-[#166534]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
                Live
              </span>
            </div>
            <div className="flex justify-center">
              <IPhonePreview config={config} size="md" />
            </div>
          </div>
        </aside>
      </div>

      {/* Modal preview mobile/tablet */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 xl:hidden" data-testid="settings-preview-modal">
          <div
            onClick={() => setPreviewOpen(false)}
            className="absolute inset-0 bg-[#111111]/40 backdrop-blur-sm"
          />
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="relative">
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="settings-preview-close"
                className="absolute -top-3 -right-3 h-9 w-9 rounded-full bg-white text-[#111111] flex items-center justify-center shadow-lg z-10"
                aria-label="Fermer"
              >
                <X size={15} strokeWidth={2} />
              </button>
              <IPhonePreview config={config} size="md" />
            </div>
          </div>
        </div>
      )}

      <div className="h-20" />
    </div>
  );
};

export default Settings;
